import { FC, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Icon,
  MenuItem,
  useToast,
} from '@chakra-ui/react';
import { captureException } from '@sentry/react';
import { BiArchive } from 'react-icons/bi';
import { useNavigate } from 'react-router-dom';
import * as ToastConfigs from '../../../../configs/toast';
import {
  FileDetailsFileFragment,
  useArchiveFileByIdMutation,
} from '../../../../graphql/generated';
import { navigateToDirectory } from '../../../../pages/file-browser-page/file-browser-page';

type Props = {
  file: FileDetailsFileFragment;
};

export const FileArchiveAction: FC<Props> = ({ file }) => {
  const navigate = useNavigate();
  const cancelRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [archiveFileByIdMutation] = useArchiveFileByIdMutation();
  const toast = useToast();

  const handleFileArchive = async () => {
    try {
      const result = await archiveFileByIdMutation({
        variables: {
          cedant: file.cedant,
          id: file.id,
        },
        refetchQueries: ['GetDirectory'],
      });

      if (result.data?.archiveFileById) {
        toast(ToastConfigs.fileArchiveSuccess);

        navigate(
          navigateToDirectory({
            cedantId: file.cedant,
            directoryId: file.parentDirectoryId,
          }),
          { replace: true }
        );
      }
    } catch (e) {
      toast(ToastConfigs.problemCompletingRequest);
      captureException(e);
    }
  };

  const handleToggleConfirmationDialog = () => setIsOpen(!isOpen);

  return (
    <>
      <MenuItem
        onClick={handleToggleConfirmationDialog}
        icon={
          <span>
            <Icon as={BiArchive} fontSize="16" color="red.600" />
          </span>
        }
        data-testid="file-archive-button"
      >
        Archive
      </MenuItem>
      <AlertDialog
        isOpen={isOpen}
        isCentered={true}
        leastDestructiveRef={cancelRef}
        onClose={handleToggleConfirmationDialog}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Archive</AlertDialogHeader>
            <AlertDialogBody>
              Are you sure? You can't undo this action.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                onClick={handleToggleConfirmationDialog}
                data-testid="cancel-file-archive-button"
              >
                Cancel
              </Button>
              <Button
                ml={3}
                colorScheme="red"
                onClick={handleFileArchive}
                data-testid="confirm-file-archive-button"
              >
                Archive
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};
